import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  useCallback,
  type CSSProperties,
  type ForwardedRef,
  type PointerEvent
} from "react";

/** Segment ids share the bar's drag context with group keys. */
export const SEGMENT_PREFIX = "seg:";
export const segmentDragId = (tabId: string): string =>
  `${SEGMENT_PREFIX}${tabId}`;

type SortableTab<T extends HTMLElement> = {
  ref: (node: T | null) => void;
  style: CSSProperties;
  isDragging: boolean;
  /** Compose with any pointerdown the element already has. */
  onPointerDown: (e: PointerEvent<T>) => void;
};

/**
 * A tile, group or segment as a sortable item in the bar's drag context.
 * Only the pointer starts a drag (keyboard moves have their own shortcut), so
 * the element keeps its own role and focus behaviour.
 */
export function useSortableTab<T extends HTMLElement>(
  id: string,
  {
    disabled = false,
    forwardedRef,
    onPointerDown
  }: {
    disabled?: boolean;
    forwardedRef?: ForwardedRef<T>;
    onPointerDown?: (e: PointerEvent<T>) => void;
  } = {}
): SortableTab<T> {
  const { setNodeRef, listeners, transform, transition, isDragging } =
    useSortable({ id, disabled });
  const ref = useCallback(
    (node: T | null) => {
      setNodeRef(node);
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef, setNodeRef]
  );
  return {
    ref,
    style: { transform: CSS.Translate.toString(transform), transition },
    isDragging,
    onPointerDown: (e) => {
      onPointerDown?.(e);
      listeners?.onPointerDown?.(e);
    }
  };
}
